import { useFormContext } from "react-hook-form";
import { useState, useMemo, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Consultation } from "@/types/consultation";
import Model, { IMuscleStats, IExerciseData, Muscle } from "react-body-highlighter";
import { useGetMeasurementsQuery } from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

export interface BodyPartDef {
  key: string;
  label: string;
  muscle: Muscle;
}

export const BODY_PART_DEFS: BodyPartDef[] = [
  { key: "right-forearm",     label: "Right Forearm",     muscle: "forearm"      },
  { key: "left-forearm",      label: "Left Forearm",      muscle: "forearm"      },
  { key: "right-upper-arm",   label: "Right Upper Arm",   muscle: "biceps"       },
  { key: "left-upper-arm",    label: "Left Upper Arm",    muscle: "biceps"       },
  { key: "chest",             label: "Chest",             muscle: "chest"        },
  { key: "bust",              label: "Bust",              muscle: "chest"        },
  { key: "midriff",           label: "Midriff",           muscle: "obliques"     },
  { key: "waist",             label: "Waist",             muscle: "obliques"     },
  { key: "abdomen",           label: "Abdomen",           muscle: "abs"          },
  { key: "hips",              label: "Hips",              muscle: "adductor"     },
  { key: "buttocks",          label: "Buttocks",          muscle: "gluteal"      },
  { key: "right-upper-thigh", label: "Right Upper Thigh", muscle: "quadriceps"   },
  { key: "left-upper-thigh",  label: "Left Upper Thigh",  muscle: "quadriceps"   },
  { key: "right-mid-thigh",   label: "Right Mid Thigh",   muscle: "quadriceps"   },
  { key: "left-mid-thigh",    label: "Left Mid Thigh",    muscle: "quadriceps"   },
  { key: "right-lower-thigh", label: "Right Lower Thigh", muscle: "abductors"    },
  { key: "left-lower-thigh",  label: "Left Lower Thigh",  muscle: "abductors"    },
  { key: "right-knee",        label: "Right Knee",        muscle: "knees"        },
  { key: "left-knee",         label: "Left Knee",         muscle: "knees"        },
  { key: "right-calf",        label: "Right Calf",        muscle: "calves"       },
  { key: "left-calf",         label: "Left Calf",         muscle: "calves"       },
  { key: "right-ankle",       label: "Right Ankle",       muscle: "right-soleus" },
  { key: "left-ankle",        label: "Left Ankle",        muscle: "left-soleus"  },
];

export const BODY_PART_BY_KEY = Object.fromEntries(
  BODY_PART_DEFS.map((d) => [d.key, d])
) as Record<string, BodyPartDef>;

export const MUSCLE_TO_PARTS: Record<string, string[]> = BODY_PART_DEFS.reduce(
  (acc, def) => {
    if (!acc[def.muscle]) acc[def.muscle] = [];
    acc[def.muscle].push(def.key);
    return acc;
  },
  {} as Record<string, string[]>
);

const MAX_MEASUREMENT_CM = 100;

const hasValue = (bm: Record<string, unknown>, key: string) =>
  bm[key] != null && !isNaN(Number(bm[key])) && Number(bm[key]) > 0;

// ─────────────────────────────────────────────────────────────────────────────

const BodyMeasurements = ({ action, open }: FormStepProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { watch, setValue } = useFormContext<Consultation>();
  const consultationId = watch("id");

  // The muscle group dialog state
  // dialogParts = list of part keys to show in the one dialog
  const [dialogParts, setDialogParts] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen]     = useState(false);

  // inline inputs: { [partKey]: string } — one input per row in the dialog
  const [inlineValues, setInlineValues] = useState<Record<string, string>>({});
  // errors: { [partKey]: errorMessage }
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: measurementsData } = useGetMeasurementsQuery(
    { consultation_id: consultationId!, org_id: orgId },
    { skip: !consultationId || !open }
  );

  useEffect(() => {
    if (measurementsData?.measurements?.length) {
      setValue(
        "body_measurement",
        measurementsData.measurements[0].body_measurement
      );
    }
  }, [measurementsData, setValue]);

  const bodyMeasurement = watch("body_measurement") || {};

  // ── Validate single field ──────────────────────────────────────────────────
  const validateField = (value: string): string | null => {
    if (!value || value.trim() === "") return null; // Empty is OK (optional)
    
    const num = parseFloat(value);
    
    if (isNaN(num)) {
      return "Please enter a valid number";
    }
    
    if (num < 0) {
      return "Measurement cannot be negative";
    }
    
    if (num === 0) {
      return "Measurement must be greater than 0";
    }

    if (num > MAX_MEASUREMENT_CM) {
      return `Measurement cannot exceed ${MAX_MEASUREMENT_CM} cm`;
    }
    
    return null; // Valid
  };

  // ── Handle input change with validation ─────────────────────────────────
  const handleInputChange = (key: string, value: string) => {
    setInlineValues((prev) => ({ ...prev, [key]: value }));
    
    const error = validateField(value);
    setErrors((prev) => {
      const updated = { ...prev };
      if (error) {
        updated[key] = error;
      } else {
        delete updated[key];
      }
      return updated;
    });
  };

  // ── exerciseData for SVG ──────────────────────────────────────────────────
  const exerciseData = useMemo<IExerciseData[]>(() => {
    const muscleMap: Record<string, { saved: number; total: number; labels: string[] }> = {};

    BODY_PART_DEFS.forEach(({ key, label, muscle }) => {
      if (!muscleMap[muscle]) muscleMap[muscle] = { saved: 0, total: 0, labels: [] };
      muscleMap[muscle].total += 1;
      if (hasValue(bodyMeasurement, key)) {
        muscleMap[muscle].saved += 1;
        muscleMap[muscle].labels.push(`${label}: ${bodyMeasurement[key]}cm`);
      }
    });

    return Object.entries(muscleMap)
      .filter(([, { saved }]) => saved > 0)
      .map(([muscle, { saved, total, labels }]) => ({
        name: labels.join(" | "),
        muscles: [muscle as Muscle],
        frequency: 1,
      }));
  }, [bodyMeasurement]);

  // ── Open the unified dialog ───────────────────────────────────────────────
  const openDialog = (parts: string[]) => {
    // Pre-fill inputs with existing saved values (as strings for the input)
    const prefilled: Record<string, string> = {};
    parts.forEach((k) => {
      prefilled[k] = hasValue(bodyMeasurement, k)
        ? String(bodyMeasurement[k])
        : "";
    });
    setInlineValues(prefilled);
    setErrors({}); // Clear errors when opening dialog
    setDialogParts(parts);
    setIsDialogOpen(true);
  };

  const handleMuscleClick = ({ muscle }: IMuscleStats) => {
    const parts = MUSCLE_TO_PARTS[muscle as string];
    if (!parts?.length) return;
    openDialog(parts);
  };

  // ── Save all inline values in one shot ───────────────────────────────────
  const handleSaveAll = () => {
    const updated = { ...bodyMeasurement };
    dialogParts.forEach((key) => {
      const raw = inlineValues[key];
      const val = parseFloat(raw);
      if (raw && val > 0 && val <= MAX_MEASUREMENT_CM) {
        updated[key] = val;
      } else {
        // If field was cleared / empty / invalid, remove existing value
        delete updated[key];
      }
    });
    setValue("body_measurement", updated);
    setIsDialogOpen(false);
    setDialogParts([]);
    setInlineValues({});
    setErrors({});
  };

  // ── Remove a single part from the dialog ─────────────────────────────────
  const handleRemoveInline = (key: string) => {
    const updated = { ...bodyMeasurement };
    delete updated[key];
    setValue("body_measurement", updated);
    setInlineValues((prev) => ({ ...prev, [key]: "" }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // ── Remove from saved list card ───────────────────────────────────────────
  const handleRemoveFromList = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = { ...bodyMeasurement };
    delete updated[key];
    setValue("body_measurement", updated);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setDialogParts([]);
    setInlineValues({});
    setErrors({});
  };

  // Dialog title: if single part use its label, else use muscle group name
  const dialogTitle = (() => {
    if (dialogParts.length === 1) {
      return `Measure ${BODY_PART_BY_KEY[dialogParts[0]]?.label}`;
    }
    // Group title from first part's label (strip Left/Right prefix)
    const first = BODY_PART_BY_KEY[dialogParts[0]]?.label || "";
    return `Measure — ${first.replace(/^(Right|Left)\s+/i, "")} Area`;
  })();

  // Is Save enabled? — at least one field must have a valid value AND no errors
  const hasErrors = Object.keys(errors).length > 0;
  const canSave = !hasErrors && dialogParts.some((k) => {
    const val = parseFloat(inlineValues[k] || "");
    return !isNaN(val) && val > 0 && val <= MAX_MEASUREMENT_CM;
  });

  // Saved items list
  const savedItems = useMemo(() => {
    return BODY_PART_DEFS.filter(({ key }) => hasValue(bodyMeasurement, key)).map(
      ({ key, label }) => ({ key, label, value: bodyMeasurement[key] as number })
    );
  }, [bodyMeasurement]);


  return (
    <div className="space-y-4">
      <style>{`
        .rbh-wrapper svg polygon {
          transition: fill 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
        }
        .rbh-wrapper svg polygon:hover {
          filter: brightness(0.7);
          opacity: 0.9;
        }
      `}</style>

      <Card className="p-4">
        <p className="font-medium text-sm">Body Measurements</p>
        <p className="text-xs text-gray-500 mt-1">
          Click on any body part in the diagram to add measurements (in centimeters).
        </p>
        <Separator className="h-[1px] mt-2" />
        
        {/* Body model view */}
        <div className="flex flex-col items-center mt-4">
          <p className="text-sm font-medium mb-2">Front View</p>
          <div className="flex justify-center items-center w-full">
            <Model
              data={exerciseData}
              onClick={handleMuscleClick}
              highlightedColors={["#22c55e"]}
              style={{ width: "100%", maxWidth: "300px", height: "450px" }}
              svgStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>

        {/* Saved measurements */}
        {savedItems.length > 0 && (
          <div className="mt-6">
            <p className="text-sm font-medium mb-3">
              Saved Measurements ({savedItems.length})
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {savedItems.map(({ key, label, value }) => (
                <div
                  key={key}
                  onClick={() => openDialog([key])}
                  className="relative flex flex-col p-3 bg-white border rounded-lg shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                >
                  <div className="flex justify-between items-start">
                    <span className="font-semibold text-gray-700 text-sm truncate pr-6" title={label}>
                      {label}
                    </span>
                    <button
                      onClick={(e) => handleRemoveFromList(key, e)}
                      className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                      type="button"
                      title="Remove"
                    >
                      <i className="fa fa-trash text-xs" />
                    </button>
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{value} cm</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* ── Unified Measurement Dialog ─────────────────────────────────────── */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-base">{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-3">
            {dialogParts.map((key) => {
              const def = BODY_PART_BY_KEY[key];
              const currentVal = inlineValues[key] ?? "";
              const alreadySaved = hasValue(bodyMeasurement, key);
              const fieldError = errors[key];

              return (
                <div key={key} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-3">
                    {/* Label */}
                    <div className="w-36 shrink-0">
                      <span className="text-sm font-medium text-gray-700">{def?.label}</span>
                      {alreadySaved && (
                        <span className="ml-1 text-[10px] text-green-600 font-semibold">✓</span>
                      )}
                    </div>

                    {/* Input */}
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        min="0"
                        max={MAX_MEASUREMENT_CM}
                        step="0.1"
                        placeholder="0.0"
                        value={currentVal}
                        onChange={(e) =>
                          handleInputChange(key, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        className={`w-full rounded-md border px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:border-0 transition-all ${
                          fieldError
                            ? "border-red-400 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        cm
                      </span>
                    </div>

                    {/* Remove button — only if this part already has a saved value */}
                    {alreadySaved && (
                      <button
                        type="button"
                        onClick={() => handleRemoveInline(key)}
                        title="Remove"
                        className="shrink-0 p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                      >
                        <i className="fa fa-trash text-xs" />
                      </button>
                    )}
                  </div>

                  {/* Inline Error Message */}
                  {fieldError && (
                    <div className="flex items-start gap-2 ml-36">
                      <i className="fa fa-exclamation-circle text-red-500 text-xs mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-red-600 font-medium">{fieldError}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter className="pt-1">
            <Button variant="outline" onClick={closeDialog} type="button">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveAll}
              disabled={!canSave}
              title={canSave ? "Save measurements" : "Fix errors before saving"}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodyMeasurements;